import React, { createContext, useMemo, useContext } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import has from 'lodash/has';
import find from 'lodash/find';
import keys from 'lodash/keys';
import pickBy from 'lodash/pickBy';
import { useReducerAsync, type AsyncActionHandlers } from 'use-reducer-async';
import { produce } from 'immer';
import reducer from './reducers';
import selector from './builder-selector';
import actions from './actions/builder-action';
import {
  moveFieldHandlers,
  insertActionHandlers,
  createSaveActionHandlers,
} from './actions';
import {
  defaultState,
  ADMIN_DEFAULT_LOGO,
  ADMIN_COLOR_LOGO,
  ADMIN_BRAND_COLOR,
  ACTION_CLEAR_METADATA_TEMPLATE,
  ACTION_NOTIFY_FILE_REQUEST_INACTIVE,
} from './builder-constant';
import { rawColorHex } from '../../../common/utils/colorHex';
import { createElementTestids, replaceUrlWithRelativeUrl } from './utils';
import { validateFormBuilderFields } from './validator/builder-validator';

import type { Resin } from '../../../common/resin';
import type { FileRequest } from '../../../types/FileRequest';
import type { MetadataTemplate } from '../../../types/Metadata';
import type { Theme } from '../../../types/Theme';
import type { BuilderAction, AsyncActions, State } from './builder-type';
import messages from '../../new-file-request-builder/builder-notification/messages';
import type { Element } from '../../../types/form/Element';

export type Props = {
  builderValidationEnabled?: boolean;
  children: React.ReactNode;
  fileRequest: FileRequest;
  isMetadataEnabled: boolean;
  metadataTemplateError: boolean;
  allMetadataTemplates: MetadataTemplate[];
  activeMetadataTemplates: MetadataTemplate[];
  multipleMetadataTemplatesEnabled: boolean;

  onDelete: () => void;
  resin: Resin;
  theme: Theme;
};

export type BuilderContextType = {
  state: State;
  dispatch: React.Dispatch<BuilderAction>;
  selector: typeof selector;
};
const BuilderContext = createContext<BuilderContextType | undefined>(undefined);

function setUploadedLogo(draftState: State) {
  const logo = selector.getThemeLogo(draftState);
  if (
    logo.key &&
    logo.value &&
    ![ADMIN_COLOR_LOGO, ADMIN_DEFAULT_LOGO].includes(logo.key)
  ) {
    draftState.themePanelState.logos.uploadedLogo = logo;
  }
}

function addSavedCustomColor(draftState: State) {
  const color = selector.getThemePrimaryColor(draftState);
  if (
    color.value &&
    color.key !== ADMIN_BRAND_COLOR &&
    !draftState.themePanelState.colors.includes(color.value)
  ) {
    draftState.themePanelState.colors.push(color.value);
  }
}

function handleMetadataTemplateError(
  draftState: State,
  metadataTemplateError: boolean,
) {
  if (metadataTemplateError) {
    draftState.metadataTemplateError = true;
    draftState.errorMessage = messages.errorMetadataTemplateNotification;
    draftState.deactivatedBadFileRequest = true;
    draftState.notificationOptions = {
      duration: '', // No timeout, notification will display until acted upon
      onCloseAction: ACTION_NOTIFY_FILE_REQUEST_INACTIVE,
      onMessageAction: {
        closeOnClick: true,
        messages: messages.clearMetadataTemplateButton,
        onClick: ACTION_CLEAR_METADATA_TEMPLATE,
      },
    };
  }
}

function setUpMetadataTemplatesFields(
  draftState: State,
  allMetadataTemplates: Array<MetadataTemplate>,
  activeMetadataTemplates: Array<MetadataTemplate>,
  multipleMetadataTemplatesEnabled: boolean,
) {
  const content = selector.getFormContent(draftState);
  if (!activeMetadataTemplates?.length) return;
  draftState.allMetadataTemplates = cloneDeep(allMetadataTemplates);
  draftState.activeMetadataTemplates = cloneDeep(activeMetadataTemplates);
  activeMetadataTemplates.forEach((metadataTemplate, i) => {
    metadataTemplate.fields.forEach((field, j) => {
      const isSelected =
        has(field, 'key') &&
        !!find(
          content.components,
          (component: Element) =>
            'metadataFieldKey' in component &&
            component.metadataFieldKey === field.key,
        );

      const activeMetadataTemplateField =
        draftState?.activeMetadataTemplates[i]?.fields[j];
      if (activeMetadataTemplateField) {
        activeMetadataTemplateField.isSelected = isSelected;
      }
    });
  });
  draftState.allMetadataFieldsSelected = multipleMetadataTemplatesEnabled
    ? !selector.hasUnselectedMetadataFields(draftState)
    : selector.getUnselectedMetadataFields(draftState).length === 0;
}

function setUpThemePanel(draftState: State, theme: Theme) {
  if (!theme) return;
  if (theme.primaryColor) {
    draftState.themePanelState.defaultColor = rawColorHex(theme.primaryColor);
  }
  if (theme.logoURLs?.small) {
    // Make image URL relative for Percy testing
    draftState.themePanelState.logos.adminColorLogo.value =
      replaceUrlWithRelativeUrl(theme.logoURLs.small);
  }
  if (theme.logo2URLs?.small) {
    // Make image URL relative for Percy testing
    draftState.themePanelState.logos.adminDefaultLogo.value =
      replaceUrlWithRelativeUrl(theme.logo2URLs.small);
  }
}

function setUpDisplayIndex(draftState: State) {
  const logics = draftState?.workingFileRequest?.form?.content?.logic;
  if (!logics) {
    return;
  }
  const containsDisplayIndex = logics.every(
    logic => logic.displayIndex !== undefined,
  );
  // If logics is empty, set displayIndex to index
  if (!containsDisplayIndex) {
    logics.forEach((logic, index) => {
      if (logic.displayIndex === undefined) {
        logic.displayIndex = index + 1;
      }
    });
  }
}

const BuilderProvider = ({
  fileRequest,
  isMetadataEnabled,
  allMetadataTemplates,
  activeMetadataTemplates,
  metadataTemplateError,
  multipleMetadataTemplatesEnabled,
  builderValidationEnabled,
  onDelete,
  resin,
  children,
  theme,
}: Props) => {
  const initialState: State = produce(defaultState, draftState => {
    draftState.isMetadataEnabled = isMetadataEnabled;
    draftState.savedFileRequest = cloneDeep(fileRequest);
    draftState.workingFileRequest = cloneDeep(fileRequest);
    draftState.resin = resin;
    draftState.resinEvent = {
      page: 'file-request-builder',
      action: 'programmatic',
      component: 'formbuilder',
      feature: 'filerequest',
    };
    draftState.oneRequiredUploadFieldRemaining =
      selector.oneRequiredUploadFieldRemaining(draftState);
    const content = selector.getFormContent(draftState);

    // Components with editable description include:
    // 1. All components with saved descriptions
    // 2. root component is always editable
    draftState.editableDescriptions = keys(
      pickBy(
        content.components,
        component =>
          !!component.description ||
          component.id === selector.getRootId(draftState),
      ),
    );

    draftState.elementInFocus = selector.getRootId(draftState);

    setUpMetadataTemplatesFields(
      draftState,
      allMetadataTemplates,
      activeMetadataTemplates,
      multipleMetadataTemplatesEnabled,
    );

    draftState.formElementTestids = createElementTestids(
      content.components,
      {},
    );

    // set up theme panel
    setUpThemePanel(draftState, theme);

    // set uploaded logo in theme panel
    setUploadedLogo(draftState);

    // add currently saved custom color to theme panel colors
    addSavedCustomColor(draftState);

    // validate validity of upload fields on initial load for Forms only
    if (builderValidationEnabled) {
      validateFormBuilderFields(draftState);
    }

    // backwards compatibility code to add Logic displayIndex to Forms that don't have this property
    setUpDisplayIndex(draftState);

    // handle metadata template error
    handleMetadataTemplateError(draftState, metadataTemplateError);
  });

  const builderActionDispatch = useMemo(
    () => actions({ onDelete }),
    [onDelete],
  );
  const moveActionDispatch = useMemo(() => moveFieldHandlers(), []);
  const insertActionDispatch = useMemo(() => insertActionHandlers(), []);
  const createSaveActionDispatch = useMemo(
    () => createSaveActionHandlers(),
    [],
  );

  const actionsDispatch: AsyncActionHandlers<
    React.Reducer<State, BuilderAction>,
    AsyncActions
  > = useMemo(
    () => ({
      ...builderActionDispatch,
      ...moveActionDispatch,
      ...insertActionDispatch,
      ...createSaveActionDispatch,
    }),
    [
      builderActionDispatch,
      insertActionDispatch,
      moveActionDispatch,
      createSaveActionDispatch,
    ],
  );

  const [state, dispatch] = useReducerAsync(
    reducer,
    initialState,
    actionsDispatch,
  );

  return (
    <BuilderContext.Provider value={{ state, dispatch, selector }}>
      {children}
    </BuilderContext.Provider>
  );
};

const useBuilderContext = () => {
  const context = useContext(BuilderContext);
  if (!context) {
    throw new Error('useBuilderContext must be used within a BuilderProvider');
  }
  return context;
};

export {
  BuilderProvider,
  useBuilderContext,
  BuilderContext, // for react testing testing
};
